import { useEffect, useMemo } from 'react'

import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import Box, { BoxProps } from '@mui/material/Box'
import { CircularProgress, ImageList, ImageListItem, ImageListItemBar, useMediaQuery, useTheme } from '@mui/material'

import { useTranslation } from 'react-i18next'

import Icon from 'src/@core/components/icon'
import CustomTextField from 'src/@core/components/mui/text-field'

import useInfiniteImages from 'src/hooks/images/useInfiniteImages'
import useGetStockImageDetails from 'src/hooks/images/useGetStockImageDetail'

const IconBox = styled(Box)<BoxProps>(({ theme }) => ({
  position: 'absolute',
  top: `-${theme.spacing(2.5)}`,
  right: `-${theme.spacing(2.5)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.customColors.cardBg,
  boxShadow: `0 2px 4px 0 ${theme.colorWithOpacity(theme.palette.grey[500], 30)}`,
  cursor: 'pointer'
}))

const ModalContent = styled(Box)<BoxProps>(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(8),
  width: 800,
  height: '100%',
  maxHeight: '80%',
  padding: theme.spacing(16),
  border: 0,
  outline: 0,
  borderRadius: theme.spacing(1.5),
  boxShadow: `0 5px 20px 0 ${theme.colorWithOpacity('#4B465C', 40)}`,
  backgroundColor: theme.palette.customColors.cardBg,
  [theme.breakpoints.down('md')]: {
    width: '93%',
    padding: theme.spacing(4)
  }
}))

const ModalHeader = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(8),
  paddingBottom: theme.spacing(2.5)
}))

const ModalFooter = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(4)
}))

type StockPhotoChooserModalProps = {
  isOpen: boolean
  onClose: () => void
  onPick: (id: string) => void
}

const StockPhotoPickerModal = ({ isOpen, onClose, onPick }: StockPhotoChooserModalProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { imageDetails, setSelectedId } = useGetStockImageDetails()
  const { data: infiniteImages, setSearchTerm, isLoading, lastElementRef } = useInfiniteImages()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (imageDetails) {
      onPick(imageDetails.urls.full)
      setSelectedId('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageDetails])

  const flatMappedinfiniteImages = useMemo(() => infiniteImages?.pages.flatMap(image => image), [infiniteImages])

  return (
    <Modal keepMounted={false} open={isOpen} onClose={onClose}>
      <ModalContent>
        <IconBox>
          <Icon icon='tabler:x' onClick={onClose} />
        </IconBox>
        <ModalHeader>
          <Typography variant='h3'>{t('THIRD_SERVICE.STOCK_IMAGE.choose_image')}</Typography>
          <CustomTextField
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{ endAdornment: <Icon icon='tabler:search' /> }}
            placeholder={t('THIRD_SERVICE.STOCK_IMAGE.search_images')}
            sx={{ maxWidth: '600px' }}
            fullWidth
          />
        </ModalHeader>
        <Box display='flex' flexDirection='column' flexGrow='1' overflow='scroll' position='relative'>
          {isLoading ? (
            <Box sx={{ mt: 6, display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
              <CircularProgress sx={{ mb: 4 }} />
              <Typography>{t('extracted.loading...')}</Typography>
            </Box>
          ) : (
            flatMappedinfiniteImages && (
              <Box overflow='hidden' flex={1} height='inherit' position='absolute'>
                <ImageList cols={isMobile ? 2 : 3} gap={isMobile ? 2 : 8}>
                  {flatMappedinfiniteImages.map(image => (
                    <ImageListItem
                      onClick={() => {
                        setSelectedId(image.id)
                      }}
                      ref={lastElementRef}
                      key={image.urls.small}
                      sx={{
                        border: '2px solid',
                        borderColor: 'transparent',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        padding: theme.spacing(1),
                        transition: 'all 200ms ease-in-out',
                        overflow: 'hidden',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          '&& img:hover': {
                            overflow: 'hidden',
                            transform: 'scale(1.3)'
                          }
                        }
                      }}
                    >
                      <Box sx={{ maxHeight: '145px', overflow: 'hidden' }}>
                        <img
                          srcSet={image.urls.small}
                          src={image.urls.small}
                          alt={image.alt_description}
                          loading='lazy'
                          style={{
                            maxHeight: '145px',
                            minWidth: '100%',
                            overflow: 'hidden',
                            transition: 'all 200ms ease-in-out'
                          }}
                        />
                      </Box>
                      <ImageListItemBar
                        title={
                          <Box display='flex' gap={theme.spacing(2)} paddingY={theme.spacing(1)}>
                            <Typography variant='caption'>
                              {t('extracted.photo_by')}{' '}
                              <Typography variant='caption' color={theme.palette.primary.main}>
                                {image.user.first_name} {image.user.last_name}
                              </Typography>
                            </Typography>
                            <svg
                              width='63'
                              height='15'
                              viewBox='0 0 63 15'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <g clipPath='url(#clip0_3241_8561)'>
                                <mask
                                  id='mask0_3241_8561'
                                  style={{ maskType: 'luminance' }}
                                  maskUnits='userSpaceOnUse'
                                  x='0'
                                  y='0'
                                  width='63'
                                  height='15'
                                >
                                  <path d='M62.2049 0.0888672H0.776855V14.0889H62.2049V0.0888672Z' fill='white' />
                                </mask>
                                <g mask='url(#mask0_3241_8561)'>
                                  <path
                                    d='M8.82484 5.28848H12.5197V11.8317H0.776855V5.28848H4.47167V8.55336H8.8114V5.28848H8.82484ZM21.32 8.63397C21.32 9.74914 20.5945 10.394 19.5868 10.394C18.606 10.394 17.8671 9.7357 17.8671 8.63397V3.36718H16.2279V8.62054C16.2279 10.6896 17.7058 12.0063 19.6003 12.0063C21.5081 12.0063 22.9726 10.6896 22.9726 8.62054V3.36718H21.3335V8.63397H21.32ZM27.1914 5.79904C26.56 5.79904 25.8613 6.06775 25.4985 6.6858V5.96027H23.9669V11.8317H25.5523V8.44587C25.5523 7.76065 25.9554 7.22322 26.654 7.22322C27.4199 7.22322 27.7423 7.73378 27.7423 8.39213V11.8182H29.3277V8.12342C29.3143 6.83359 28.6425 5.79904 27.1914 5.79904ZM33.0091 8.27121C33.0091 8.27121 32.4717 8.16372 32.1358 8.09654C31.7999 8.02937 31.5984 7.8547 31.5984 7.57255C31.5984 7.23666 31.8939 6.99482 32.3104 6.99482C32.9016 6.99482 33.13 7.2904 33.1838 7.65317H34.5542C34.5408 6.84702 33.9093 5.79904 32.3373 5.79904C31.0744 5.79904 30.1473 6.67236 30.1473 7.72035C30.1473 8.53992 30.6579 9.22514 31.7865 9.46699L32.5926 9.64165C33.0494 9.7357 33.2241 9.95067 33.2241 10.2194C33.2241 10.5284 32.9419 10.7971 32.4179 10.7971C31.7865 10.7971 31.4371 10.4344 31.3565 10.0178H29.9458C30.0264 10.8912 30.6579 12.0063 32.4314 12.0063C33.963 12.0063 34.7423 11.0255 34.7423 10.0716C34.7557 9.21171 34.1646 8.51305 33.0091 8.27121ZM41.5005 8.87582C41.5005 10.6493 40.3853 11.966 38.7327 11.966C37.9266 11.966 37.322 11.6436 37.0398 11.2539V14.0889H35.4544V5.96027H36.9861V6.6858C37.2548 6.22898 37.8997 5.82591 38.7865 5.82591C40.5062 5.82591 41.5005 7.14261 41.5005 8.87582ZM39.9419 8.88925C39.9419 7.8547 39.297 7.2501 38.4909 7.2501C37.6847 7.2501 37.0264 7.8547 37.0264 8.88925C37.0264 9.9238 37.6847 10.5418 38.4909 10.5418C39.297 10.5418 39.9419 9.93724 39.9419 8.88925ZM49.0782 5.96027H50.6099V11.8317H49.0245V11.1061C48.7557 11.5764 48.1377 11.966 47.3316 11.966C45.679 11.966 44.5638 10.6493 44.5638 8.87582C44.5638 7.12917 45.558 5.82591 47.2644 5.82591C48.1511 5.82591 48.7826 6.22898 49.0648 6.6858V5.96027H49.0782ZM49.0513 8.88925C49.0513 7.8547 48.393 7.2501 47.5868 7.2501C46.7807 7.2501 46.1358 7.8547 46.1358 8.88925C46.1358 9.93724 46.7807 10.5418 47.5868 10.5418C48.393 10.5418 49.0513 9.9238 49.0513 8.88925ZM42.2394 11.8182H43.8248V3.36718H42.2394V11.8182ZM60.0283 5.79904C59.464 5.79904 58.8191 5.98714 58.4429 6.44395V3.36718H56.8575V11.8182H58.4429V8.37869C58.4832 7.73378 58.8728 7.23666 59.5446 7.23666C60.3104 7.23666 60.6329 7.74722 60.6329 8.40557V11.8317H62.2183V8.13685C62.2049 6.83359 61.5197 5.79904 60.0283 5.79904ZM54.3853 8.27121C54.3853 8.27121 53.8479 8.16372 53.512 8.09654C53.1761 8.02937 52.9746 7.8547 52.9746 7.57255C52.9746 7.23666 53.2701 6.99482 53.6866 6.99482C54.2778 6.99482 54.5062 7.2904 54.56 7.65317H55.9304C55.917 6.84702 55.2855 5.79904 53.7135 5.79904C52.4506 5.79904 51.5235 6.67236 51.5235 7.72035C51.5235 8.53992 52.0341 9.22514 53.1627 9.46699L53.9688 9.64165C54.4256 9.7357 54.6003 9.95067 54.6003 10.2194C54.6003 10.5284 54.3181 10.7971 53.7941 10.7971C53.1627 10.7971 52.8133 10.4344 52.7327 10.0178H51.322C51.4026 10.8912 52.0341 12.0063 53.8076 12.0063C55.3392 12.0063 56.1185 11.0255 56.1185 10.0716C56.1319 9.21171 55.5408 8.51305 54.3853 8.27121ZM8.82484 0.0888672H4.47167V3.36718H8.8114V0.0888672H8.82484Z'
                                    fill='#415BF5'
                                  />
                                </g>
                              </g>
                              <defs>
                                <clipPath id='clip0_3241_8561'>
                                  <rect
                                    width='61.428'
                                    height='14'
                                    fill='white'
                                    transform='translate(0.776855 0.0888672)'
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </Box>
                        }
                        position='below'
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            )
          )}
        </Box>
        <ModalFooter>
          <Button variant='outlined' color='primary' onClick={onClose}>
            {t('ACTION.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default StockPhotoPickerModal
